import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
} from 'react'
import cx from 'classnames'
import { useMatch, useResolvedPath } from 'react-router'
import { RawLink } from '@/components/Link'
import { Icon } from '@/components/Icon'
import {
  Disclosure as AriaDisclosure,
  DisclosurePanel as AriaDisclosurePanel,
  Heading as AriaHeading,
} from 'react-aria-components'
import { RawButton } from '@/components/Button'
import styles from './WizardNav.module.css'
import { getScrollParent } from '@react-aria/utils'
import { m } from 'motion/react'
import { useScrollFadeMaskImage } from '@/hooks/scroll-fade'
import { useScrollOffsets } from '@/hooks/element-hooks'
import {
  AutoSkeleton,
  IsSkeletonContext,
  SkeletonOverlay,
  useIsSkeleton,
} from '@/components/Skeleton'
import { getScrollTopForCentering } from '@/utils/browser-util'

interface WizardNavTabGroupProps {
  children?: ReactNode
  /**
   * A {@link TabList} instance.
   */
  tabs: ReactNode
}

/**
 * Wizard Nav Tab Group
 */
export function WizardNavTabGroup({ tabs, children }: WizardNavTabGroupProps) {
  return (
    <div className={styles.tabGroup}>
      <div className={styles.tabGroupTabs}>{tabs}</div>
      <div className={styles.tabGroupPanel}>{children}</div>
    </div>
  )
}

export interface WizardNavProps {
  children?: ReactNode
  'aria-labelledby'?: string
  'aria-label'?: string
}

/**
 * Wizard navigation - top level nav element
 */
export function WizardNav({ children, ...rest }: WizardNavProps) {
  return (
    <nav className={styles.nav} {...rest}>
      {children}
    </nav>
  )
}

export interface WizardNavScrollerProps {
  children?: ReactNode
  spacingY?: 'default' | 'xl'
}

/**
 * Wizard navigation scroller - handles scrolling and fade effects
 */
export function WizardNavScroller({
  children,
  spacingY = 'default',
}: WizardNavScrollerProps) {
  const { scrollYStart, scrollYEnd, connectRef } = useScrollOffsets()
  const { maskImage } = useScrollFadeMaskImage({
    fade: spacingY === 'xl' ? 'both' : 'bottom',
    fadeSegmentHeight:
      spacingY === 'xl' ? 'var(--spacing-4xl)' : 'var(--spacing-lg)',
    scrollYStart,
    scrollYEnd,
  })

  return (
    <m.div
      className={cx(styles.scroller, spacingY === 'xl' && styles.xl)}
      ref={connectRef}
      style={{ maskImage }}
    >
      {children}
    </m.div>
  )
}

const WizardNavListVariantContext =
  createContext<WizardNavListProps['variant']>('default')

interface WizardNavListProps {
  children?: ReactNode
  variant?: 'default' | 'flat'
}

/**
 * List of nav items.
 */
export function WizardNavList({
  children,
  variant = 'default',
}: WizardNavListProps) {
  return (
    <ul className={styles.list}>
      <WizardNavListVariantContext value={variant}>
        {children}
      </WizardNavListVariantContext>
    </ul>
  )
}

interface WizardNavListItemProps {
  label: string
  to?: string
  onPress?: () => unknown
  replace?: boolean
  isActive?: boolean
  isDisabled?: boolean
  isCompleted?: boolean
  isSkeleton?: boolean
  isRequired?: boolean
  testId?: string
}

/**
 * Wizard nav item.
 */
export function WizardNavListItem({
  to,
  onPress,
  label,
  isActive,
  replace,
  isDisabled,
  isCompleted,
  isSkeleton,
  isRequired,
  testId,
}: WizardNavListItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  // Checks whether the tab is active based on the pathname.
  const resolved = useResolvedPath(to ?? '/')
  const activeBasedOnRoute = !!useMatch(resolved.pathname)
  const ref = useRef<HTMLLIElement>(null)

  const variant = useContext(WizardNavListVariantContext)

  // Only use the active state from the route if we're using a `to` prop, and if
  // `active` is not explicitly set.
  isActive =
    (to && isActive === undefined ? activeBasedOnRoute : isActive) ?? false

  const icon = isCompleted ? (
    <Icon
      skeleton={isSkeleton}
      variant={
        isActive ? 'active' : variant === 'flat' ? 'success' : 'secondary'
      }
      name="done"
    />
  ) : isActive || variant === 'flat' ? (
    <Icon
      skeleton={isSkeleton}
      variant={isActive ? 'active' : 'secondary'}
      name="chevron"
    />
  ) : null

  // When a tab becomes active, scroll it into view.
  useEffect(() => {
    const navItemElement = ref.current
    if (!isActive || !navItemElement) {
      return
    }

    // The scroll container is the content fader.
    const parent = getScrollParent(navItemElement)

    if (parent.scrollHeight === parent.clientHeight) {
      // Not overflowing.
      return
    }

    // Calculate the new "scroll top", ensuring the active
    // nav item scrolls to be centered.
    // NOTE: We're not using `scrollIntoView` because it scrolls
    // the entire viewport, not just the scrolling element.
    const newScrollTop = getScrollTopForCentering(parent, navItemElement)

    const frame = requestAnimationFrame(() => {
      parent.scrollTop = newScrollTop
    })
    return () => cancelAnimationFrame(frame)
  }, [isActive])

  return (
    <li className={styles.item} ref={ref}>
      <RawLink
        onPress={onPress}
        to={to}
        replace={replace}
        testId={testId}
        isDisabled={isDisabled || isSkeleton}
        aria-label={
          isCompleted
            ? `${label} (completed)`
            : isRequired
              ? `${label} (required)`
              : label
        }
        aria-current={isActive ? 'step' : 'page'}
        className={({ isFocusVisible, isHovered, isDisabled }) =>
          cx(
            styles.itemLink,
            variant === 'flat' ? styles.itemLinkFlat : styles.itemLinkDefault,
            isHovered && styles.hover,
            isDisabled && styles.disabled,
            isActive &&
              (variant === 'flat'
                ? styles.itemLinkFlatActive
                : styles.itemLinkDefaultActive),
            isFocusVisible && styles.focusVisible,
            isSkeleton && styles.skeleton,
          )
        }
      >
        <div className={styles.itemLabel}>
          <AutoSkeleton isSkeleton={isSkeleton}>
            <span>
              {label}
              {isRequired && (
                <>
                  <span className={styles.itemRequiredIndicator} />{' '}
                  <span
                    data-test={`${testId}-required-indicator`}
                    className="sr-only"
                  >
                    (required)
                  </span>
                </>
              )}
              {isCompleted && (
                <>
                  {' '}
                  <span
                    data-test={`${testId}-completed-indicator`}
                    className="sr-only"
                  >
                    (completed)
                  </span>
                </>
              )}
            </span>
          </AutoSkeleton>
        </div>
        <div className={styles.itemIcon}>{icon}</div>
      </RawLink>
    </li>
  )
}

interface WizardNavDisclosureProps {
  title: string
  isExpanded?: boolean
  onPress?: () => unknown
  children?: ReactNode
  extras?: ReactNode
  isSkeleton?: boolean
}

/**
 * Wizard Nav Disclosure
 */
export function WizardNavDisclosure({
  title,
  children,
  isExpanded,
  onPress,
  extras,
  isSkeleton,
}: WizardNavDisclosureProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <IsSkeletonContext value={isSkeleton}>
      <AriaDisclosure isExpanded={isExpanded} className={styles.disclosure}>
        <AriaHeading>
          <RawButton
            onPress={onPress}
            slot="trigger"
            className={({ isFocusVisible, isHovered, isPressed }) =>
              cx(
                styles.disclosureButton,
                isFocusVisible && styles.focusVisible,
                isHovered && styles.hover,
                isPressed && styles.pressed,
              )
            }
          >
            <div className={styles.disclosureButtonLabel}>
              <AutoSkeleton isSkeleton={isSkeleton}>{title}</AutoSkeleton>
            </div>
            {extras && (
              <div className={styles.disclosureButtonExtras}>{extras}</div>
            )}

            <div className={styles.disclosureButtonIcon}>
              <Icon
                variant="active"
                name="chevron"
                rotate={isExpanded ? 90 : 0}
              />
            </div>
          </RawButton>
        </AriaHeading>
        <AriaDisclosurePanel>{children}</AriaDisclosurePanel>
      </AriaDisclosure>
    </IsSkeletonContext>
  )
}

interface WizardNavDisclosureCompletionBadgeProps {
  current: number
  total: number
  isSkeleton?: boolean
}

/**
 * Wizard Nav Disclosure Completion Badge
 */
export function WizardNavDisclosureCompletionBadge({
  current,
  total,
  isSkeleton,
}: WizardNavDisclosureCompletionBadgeProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <div
      className={styles.disclosureCompletionBadge}
      aria-label={
        isSkeleton ? 'Placeholder' : `(${current} of ${total} completed)`
      }
    >
      {isSkeleton && <SkeletonOverlay />}

      <span aria-hidden className={styles.disclosureCompletionBadgeNumber}>
        {current}
      </span>
      <span aria-hidden>{' / '}</span>
      <span aria-hidden className={styles.disclosureCompletionBadgeNumber}>
        {total}
      </span>
    </div>
  )
}

interface ModalDialogWizardNavContentProps {
  children?: ReactNode
  /**
   * Spacing to use for the content on the x-axis.
   * Configure this based on the kind of wizard nav is being used.
   */
  spacingX?: 'default' | 'tabs' | 'flat'

  /**
   * Spacing to use for the content on the y-axis.
   * Configure this based on how much you want the scroll fade to
   * eat into the modal dialog's header and footers spacing.
   */
  spacingY?: 'default' | 'gapless' | 'gapless-pull-up'
}

/**
 * A component for use within a {@link ModalDialog} when using
 * a {@link WizardNav} in a dialog.
 */
export function ModalDialogWizardNavContent({
  children,
  spacingX = 'default',
  spacingY = 'default',
}: ModalDialogWizardNavContentProps) {
  return (
    <div
      className={cx(
        styles.modalDialogContent,
        spacingX === 'tabs' && styles.withTabs,
        spacingX === 'flat' && styles.withFlatList,
        spacingY === 'gapless' && styles.gapless,
        spacingY === 'gapless-pull-up' && styles.gaplessPullUp,
      )}
    >
      {children}
    </div>
  )
}
